/* Young Value Co. preview behaviour. Delegated listeners only; safe across Theme Editor section reloads. */
(() => {
  if (window.yvPreviewInit) return;
  window.yvPreviewInit = true;

  // Demo claim: never touches cart/checkout, only explains that nothing happened.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-yv-preview-claim]');
    if (!button) return;
    const root = button.closest('[data-yv-product]');
    const status = root?.querySelector('[data-yv-claim-status]');
    if (!status) return;
    status.textContent = 'Preview only. No order was placed and no number was reserved.';
    status.focus();
  });
})();

// Mobile sticky claim bar: only visible once the main claim button has scrolled out of view.
if (!customElements.get('yv-sticky-bar')) {
  customElements.define('yv-sticky-bar', class extends HTMLElement {
    connectedCallback() {
      const target = this.closest('[data-yv-product]')?.querySelector('.yv-pdp__claim');
      if (!target || !('IntersectionObserver' in window)) return;
      // Viewport extended far below: "not intersecting" only means the button has passed the top,
      // so fast scroll jumps from below to above still flip the state.
      this.observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(target);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('yv-reveal')) {
  customElements.define('yv-reveal', class extends HTMLElement {
    connectedCallback() {
      if (this.onInput) return;
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.onInput = () => {
        const value = Math.max(0, Math.min(100, Number(this.range.value)));
        this.style.setProperty('--yv-split', `${value}%`);
        this.range.setAttribute('aria-valuetext', `${value} percent wet view`);
      };
      this.range.addEventListener('input', this.onInput);
      this.onInput();
      this.rainBtn = this.querySelector('.yv-reveal__rain-btn');
      this.onRain = () => this.rain();
      this.rainBtn?.addEventListener('click', this.onRain);
    }

    // Rain mode: rain falls over the photo while the wet state washes in, holds, then dries back.
    rain() {
      if (this.classList.contains('is-raining')) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const tween = (to, ms) => new Promise((done) => {
        const from = Number(this.range.value), t0 = performance.now();
        const step = (t) => {
          const k = reduce ? 1 : Math.min(1, (t - t0) / ms);
          this.range.value = from + (to - from) * (1 - Math.pow(1 - k, 3));
          this.onInput();
          k < 1 ? requestAnimationFrame(step) : done();
        };
        requestAnimationFrame(step);
      });
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      this.classList.add('is-raining');
      this.rainBtn.setAttribute('aria-pressed', 'true');
      this.range.value = 0; this.onInput();
      tween(100, 2600)
        .then(() => wait(1800))
        .then(() => { this.classList.remove('is-raining'); return wait(600); })
        .then(() => tween(0, 3200))
        .then(() => this.rainBtn.setAttribute('aria-pressed', 'false'));
    }

    disconnectedCallback() {
      this.range?.removeEventListener('input', this.onInput);
      this.rainBtn?.removeEventListener('click', this.onRain);
      this.onInput = null;
    }
  });
}

// The product-page reveal is server-rendered for one colour: re-render it when the colour changes
// (the section hides itself when that colour has no dry/wet pair).
document.addEventListener('on:variant:change', (event) => {
  const id = event.detail?.variant?.id;
  const slot = document.querySelector('.yv-reveal-slot');
  if (!id || !slot || slot.dataset.variant === String(id)) return;
  slot.dataset.variant = id;
  const wrap = document.getElementById(`shopify-section-${slot.dataset.section}`);
  fetch(`${window.location.pathname}?variant=${id}&section_id=${slot.dataset.section}`)
    .then((res) => (res.ok ? res.text() : Promise.reject()))
    .then((html) => {
      const next = new DOMParser().parseFromString(html, 'text/html').getElementById(wrap.id);
      if (next) wrap.innerHTML = next.innerHTML;
    })
    .catch(() => {});
});

// 2 + 1 offer: "3 jackets" adds the three slot variants in one request.
// Pricing comes from the store's automatic "Buy 2, get 1 free" discount, not from this script.
if (!customElements.get('yv-bundle')) {
  customElements.define('yv-bundle', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-yv-bundle-add]');
      this.status = this.querySelector('[data-yv-bundle-status]');
      this.onClick = () => this.add();
      this.button?.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
    }

    async add() {
      const n = Number(this.querySelector('input[type="radio"]:checked')?.value || 3);
      const ids = [...this.querySelectorAll('[data-yv-slot]')].slice(0, n).map((s) => Number(s.value)).filter(Boolean);
      if (!ids.length) return;
      const items = Object.values(ids.reduce((acc, id) => ((acc[id] = acc[id] || { id, quantity: 0 }).quantity++, acc), {}));
      this.button.setAttribute('aria-disabled', 'true');
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || this.dataset.msgError || 'Could not add to bag');
        this.status.textContent = this.dataset.msgAdded || 'Added to your bag.';
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: this.button } }));
      } catch (error) {
        this.status.textContent = error.message;
      } finally {
        this.button.removeAttribute('aria-disabled');
      }
    }
  });
}

// Rain mode on colour cards: the tap button toggles the wet twin (hover does it in CSS on desktop).
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-yv-card-rain]');
  if (!btn) return;
  const wet = btn.closest('.yv-card').classList.toggle('is-wet');
  btn.setAttribute('aria-pressed', String(wet));
});

// Social proof from REAL recent orders (shop metafield young_value.recent_orders, written by Shopify Flow).
// Bottom left, first after 60 s, then every 60 s, max 2 per session; skips orders older than 7 days.
if (!customElements.get('yv-social-proof')) {
  customElements.define('yv-social-proof', class extends HTMLElement {
    connectedCallback() {
      let items = [];
      try { items = JSON.parse(this.dataset.items || '[]'); } catch (e) { return; }
      const week = 7 * 24 * 3600 * 1000;
      this.items = items.filter((o) => o && o.animal && o.at && Date.now() - Date.parse(o.at) < week);
      if (!this.items.length) return;
      this.querySelector('.yv-sp__close')?.addEventListener('click', () => this.hide(true));
      this.timer = setInterval(() => this.next(), 60000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    seen(n) {
      try { if (n !== undefined) sessionStorage.setItem('yv-sp', String(n)); return Number(sessionStorage.getItem('yv-sp') || 0); } catch (e) { return 0; }
    }

    ago(at) {
      const min = Math.max(1, Math.round((Date.now() - Date.parse(at)) / 60000));
      if (min < 60) return this.dataset.minutes.replace('%n', min);
      if (min < 48 * 60) return this.dataset.hours.replace('%n', Math.round(min / 60));
      return this.dataset.days.replace('%n', Math.round(min / 1440));
    }

    next() {
      const shown = this.seen();
      if (shown >= 2) return clearInterval(this.timer);
      const o = this.items[shown % this.items.length];
      const msg = (o.city ? this.dataset.msg : this.dataset.msgNocity).replace('%city', o.city || '').replace('%animal', o.animal);
      this.querySelector('.yv-sp__text').textContent = `${msg} · ${this.ago(o.at)}`;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
      this.seen(shown + 1);
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), 7000);
    }

    hide(stop) {
      this.classList.remove('is-visible');
      if (stop) { this.seen(2); clearInterval(this.timer); }
    }
  });
}
